import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { makeUseServiceCall } from "../hooks/useServiceCall";
import { OrganizationResponse } from "@akitabox/api-client";
import { api } from "../api";
import {
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Drawer,
  Grid,
  IconButton,
  ListItemIcon,
  ListItem,
  ListItemText,
  List,
  Button,
  styled,
  CardHeader,
  Box,
  ListItemButton,
  Popover,
  Tooltip,
} from "@mui/material";
import {
  CloudUpload,
  Info,
  FilterList,
  ErrorOutline,
} from "@mui/icons-material";
import { BulkAddDialog } from "../bulk-add-dialog/BulkAddDialog";

const ListItemHover = styled(ListItem)`
  &:hover {
    background-color: #f0f0f0;
    cursor: pointer;
  }
`;

type ImportSideDrawerProps = {
  open: boolean;
  onClose: () => void;
  organization: OrganizationResponse;
  onImportSelect?: (createdDate: Date, endDate: Date) => void;
};

const ImportDrawer: FunctionComponent<ImportSideDrawerProps> = ({
  open,
  onClose,
  organization,
  onImportSelect,
}) => {
  // State for BulkAddDialog
  const [bulkAddOpen, setBulkAddOpen] = useState(false);
  // Add state for opening BulkAddDialog on results step for a specific import
  const [bulkAddResultsOpen, setBulkAddResultsOpen] = useState(false);
  const [bulkAddResultsImportId, setBulkAddResultsImportId] = useState<
    string | null
  >(null);

  const {
    data: importListResponse,
    isLoading: isProcessingImportList,
    mutate: refetchImports,
  } = makeUseServiceCall(
    api.myImports.list,
    open
  )({
    targetEntity: "organization",
    targetId: organization._id,
  });

  const importList = useMemo(() => {
    if (importListResponse) {
      return importListResponse?.data.data || [];
    }
    return [];
  }, [importListResponse]);

  // Cancel import
  const [cancelImportId, setCancelImportId] = useState("");
  const { data: cancelJobResponse } = makeUseServiceCall(
    api.imports.cancelImport,
    !!cancelImportId
  )({
    importId: cancelImportId,
  });

  const handleCancelImport = async (importId: string) => {
    if (importId) {
      setCancelImportId(importId);
    }
  };
  useEffect(() => {
    if (cancelJobResponse) {
      setCancelImportId("");
      refetchImports();
    }
  }, [cancelJobResponse, refetchImports]);

  // File info popover
  const [anchorElInfo, setAnchorElInfo] = useState<null | HTMLElement>(null);
  const openInfo = Boolean(anchorElInfo);
  const idInfo = openInfo ? "simple-popper" : undefined;
  const [importInfo, setImportInfo] = useState<any | null>(null);
  const handleInfoClick = (event: React.MouseEvent<HTMLElement>, item: any) => {
    setAnchorElInfo(anchorElInfo ? null : event.currentTarget);
    setImportInfo(item);
  };

  const renderListItems = (list: any[]) => {
    if (list.length === 0) {
      return (
        <ListItem>
          <ListItemText>No recent imports found.</ListItemText>
        </ListItem>
      );
    }
    const sortedList = list.sort(
      (a, b) =>
        new Date(b.created_date).getTime() - new Date(a.created_date).getTime()
    );
    return sortedList
      .map((item: any) => {
        const createdDate = new Date(item.created_date);
        const completedDate = item.completed_date
          ? new Date(item.completed_date)
          : undefined;
        const erroredDate = item.errored_date
          ? new Date(item.errored_date)
          : undefined;
        const canceledDate = item.canceled_date
          ? new Date(item.canceled_date)
          : undefined;
        let file_name = item.file_key?.split("/").pop();
        if (item.status === "processing") {
          file_name = "Processing";
        } else if (item.status === "errored") {
          file_name = "Error during import";
        } else {
          switch (item.import_entity) {
            case "asset_photos_files":
              file_name = "Photos Import";
              break;
            case "asset_cost_lines":
              file_name = "Lifecycle Import";
              break;
            case "cost_lines":
              file_name = "Lifecycle Import";
              break;
            case "assets":
              file_name = "Assets Import";
              break;
            default:
              break;
          }
        }

        const formattedDate = `${createdDate.toLocaleDateString(
          "en-US"
        )} ${createdDate.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })}`;

        return (
          <ListItemHover
            key={item.id}
            // When the row is clicked, open the BulkAddDialog for results
            onClick={() => {
              setBulkAddResultsImportId(item.id);
              setBulkAddResultsOpen(true);
            }}
          >
            {/* Cancel icon button for processing imports */}
            {item.status === "processing" && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancelImport(item.id);
                }}
                title="Cancel Import"
                size="small"
                sx={{ ml: 1 }}
              >
                <CloudUpload />
              </IconButton>
            )}
            {/* Info or Error icon button, spaced from text */}
            <Box sx={{ display: "flex", alignItems: "center", mr: 1 }}>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleInfoClick(e, item);
                }}
                title={
                  item.status === "errored"
                    ? "Import Error Info"
                    : "Import Info"
                }
                size="small"
                sx={{ ml: 1 }}
              >
                {item.status === "errored" ? (
                  <ErrorOutline color="error" />
                ) : (
                  <Info />
                )}
              </IconButton>
            </Box>
            <ListItemText
              primary={
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                    {file_name}
                  </Typography>
                  {item.status === "processing" && (
                    <CircularProgress size={20} sx={{ marginLeft: 1 }} />
                  )}
                </Box>
              }
              secondary={
                <>
                  <Typography variant="caption">
                    {`Created: ${formattedDate}`}
                  </Typography>
                </>
              }
              style={{
                cursor: "default",
                overflowY: "hidden",
              }}
            />
            {/* Filter icon button on the right with tooltip */}
            <Tooltip
              title="Filter list by the time period of this import"
              arrow
            >
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  if (onImportSelect && createdDate && completedDate) {
                    onImportSelect(createdDate, completedDate);
                  } else if (onImportSelect && createdDate && erroredDate) {
                    onImportSelect(createdDate, erroredDate);
                  } else if (onImportSelect && createdDate && canceledDate) {
                    onImportSelect(createdDate, canceledDate);
                  }
                }}
                size="small"
                sx={{ ml: 2 }}
              >
                <FilterList />
              </IconButton>
            </Tooltip>
          </ListItemHover>
        );
      })
      .slice(0, 10);
  };

  // Poll for import list updates while drawer is open
  useEffect(() => {
    if (!open) return;
    let interval: NodeJS.Timeout | null = null;
    interval = setInterval(() => {
      refetchImports();
    }, 2000); // poll every 2 seconds
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [open, refetchImports]);

  return (
    <Drawer
      variant="permanent"
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        position: "relative",
        "& .MuiDrawer-paper": {
          position: "relative",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        },
        width: 400,
      }}
    >
      <Grid
        container
        spacing={2}
        columns={1}
        direction="column"
        sx={{
          width: 400,
          paddingLeft: 2,
          paddingRight: 2,
          height: "100%",
          overflowY: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Grid item xs={12}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: 1,
              transition: "0.3s cubic-bezier(.47,1.64,.41,.8)",
              "&:hover": {
                boxShadow: "0 4px 20px 0 rgba(0,0,0,0.12)",
                transform: "scale(1.02)",
              },
            }}
          >
            <CardHeader
              title="Import Asset Data"
              action={<Button onClick={onClose}>Close</Button>}
            />
            <CardContent>
              <List>
                <ListItemButton
                  onClick={() => setBulkAddOpen(true)}
                  sx={{ mb: 2 }}
                >
                  <ListItemIcon>
                    <CloudUpload />
                  </ListItemIcon>
                  Bulk Add or Update Assets
                </ListItemButton>
              </List>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Card
            variant="outlined"
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              borderRadius: 1,
              transition: "0.3s cubic-bezier(.47,1.64,.41,.8)",
              "&:hover": {
                boxShadow: "0 4px 20px 0 rgba(0,0,0,0.12)",
                transform: "scale(1.02)",
              },
            }}
          >
            <CardHeader
              title={"Import List"}
              subheader={"10 most recent imports"}
            />
            <CardContent
              sx={{
                flexGrow: 1,
                overflowY: "auto",
              }}
            >
              <List sx={{ flexGrow: 1, overflowY: "auto" }}>
                {isProcessingImportList && (
                  <ListItem>
                    <ListItemIcon>
                      <CircularProgress size={20} />
                    </ListItemIcon>
                    <ListItemText>Processing...</ListItemText>
                  </ListItem>
                )}
                {importList && renderListItems(importList)}
              </List>
            </CardContent>
          </Card>
        </Grid>
        <Grid item></Grid>
      </Grid>
      {bulkAddOpen && (
        <BulkAddDialog
          open={true}
          onClose={() => setBulkAddOpen(false)}
          model="assets"
          organizationId={organization._id}
          // dont show button since we are already in ELV
          orgSubdomain={undefined}
        />
      )}
      {bulkAddResultsOpen && bulkAddResultsImportId && (
        <BulkAddDialog
          open={true}
          onClose={() => {
            setBulkAddResultsOpen(false);
            setBulkAddResultsImportId(null);
          }}
          model="assets"
          organizationId={organization._id}
          orgSubdomain={undefined}
          initialStep={3} // Results step
          initialImportId={bulkAddResultsImportId}
        />
      )}
      {openInfo && (
        <Popover
          id={idInfo}
          open={openInfo}
          anchorEl={anchorElInfo}
          anchorOrigin={{
            vertical: "center",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "center",
            horizontal: "right",
          }}
          onClose={() => setAnchorElInfo(null)}
        >
          <Card>
            <CardContent>
              <Typography variant="h6">Import Information</Typography>
              {importInfo ? (
                <>
                  <Typography variant="body2">
                    Import Entity: {importInfo.import_entity}
                  </Typography>
                  <Typography variant="body2">
                    Status: {importInfo.status}
                  </Typography>
                  <Typography variant="body2">
                    Created on:{" "}
                    {importInfo.created_date
                      ? new Date(importInfo.created_date).toLocaleString()
                      : ""}
                  </Typography>
                  <Typography variant="body2">
                    {importInfo.status === "errored"
                      ? "There was an error during import."
                      : ""}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2">
                  No import information available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Popover>
      )}
    </Drawer>
  );
};

export default ImportDrawer;
